import { reportError } from "$lib/errorHandling/errorReporter";
import { logger } from "$lib/utils/logger";

class DateHelper {
  static serverTimeOffset = 0;
  static lastServerTimestamp: string | null = null;

  static getFormattedTime(
    timezone: string | undefined,
    language: string,
    date?: Date
  ): string {
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
      timeZone: timezone ?? this.deviceTimezone(),
    };
    return new Intl.DateTimeFormat(this.localeMap(language), options).format(
      date ?? this.newDateWithServerOffset()
    );
  }

  static getFormattedDate(
    timezone: string | undefined,
    language: string,
    date?: Date
  ): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: timezone ?? this.deviceTimezone(),
    };
    return new Intl.DateTimeFormat(this.localeMap(language), options).format(
      date ?? this.newDateWithServerOffset()
    );
  }

  static getDayOfWeekOffsetFromToday(
    offset: number,
    weekdayOption: "long" | "short" | "narrow" = "short",
    language: string = "en"
  ): string {
    const targetDate = new Date(this.nowWithServerOffset());
    targetDate.setDate(targetDate.getDate() + offset);
    return targetDate.toLocaleDateString(this.localeMap(language), {
      weekday: weekdayOption,
    });
  }

  static updateServerOffset(timestamp?: string) {
    if (!timestamp) {
      logger.debug("[DateHelper] No timestamp provided");
      return;
    }
    const serverDate = new Date(timestamp);
    if (isNaN(serverDate.getTime())) {
      reportError(`[DateHelper] Invalid timestamp (${timestamp})`);
      return;
    }
    if (this.lastServerTimestamp === timestamp) {
      logger.debug("[DateHelper] Timestamp already set, skipping update");
      return;
    }
    this.lastServerTimestamp = timestamp;
    this.serverTimeOffset = serverDate.getTime() - Date.now();
    logger.debug("[DateHelper] Server time offset: ", this.serverTimeOffset);
  }

  static newDateWithServerOffset(): Date {
    return new Date(this.nowWithServerOffset());
  }

  static nowWithServerOffset(): number {
    return Date.now() + this.serverTimeOffset;
  }

  private static deviceTimezone(): string {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    return timeZone;
  }

  private static localeMap(language: string): string {
    const lookup: { [key: string]: string } = {
      en: "en-US",
      fr: "fr-CA",
      es: "es-MX",
    };
    return lookup[language] ?? "en-US";
  }
}

export { DateHelper };
